/** How long to park an upload attempt waiting for connectivity before giving up on the wait. */
export const DEFAULT_OFFLINE_WAIT_CEILING_MS = 2 * 60 * 1000;

/**
 * Park until the browser reports it is back online, so a retry budget isn't
 * burned firing requests into a dead radio.
 *
 * - Resolves immediately when online, or when there is no online signal to read
 *   (`navigator.onLine` only reliably reports `false` — a `true`/absent value is
 *   treated as "go ahead and try").
 * - When offline, waits for the `online` event, up to `maxWaitMs`, then resolves
 *   anyway (best-effort — let the caller attempt and fail normally rather than
 *   hang forever).
 * - Rejects with an `AbortError` only if `signal` fires, so callers that already
 *   treat `AbortError` as a cancellation don't need special handling.
 *
 * `onWait` fires only when we actually start parking (i.e. we are offline).
 */
export function waitForOnline(
  maxWaitMs: number = DEFAULT_OFFLINE_WAIT_CEILING_MS,
  signal?: AbortSignal,
  onWait?: () => void,
): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    return Promise.resolve();
  }
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return Promise.resolve();
  }
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Upload cancelled', 'AbortError'));
  }

  onWait?.();

  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
    };
    const onOnline = () => {
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Upload cancelled', 'AbortError'));
    };

    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, maxWaitMs);
    window.addEventListener('online', onOnline);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
